/**
 * VariantMatrixView - Variant completion grid
 * USER-CON-001 UI-003: Grid showing variant coverage across issues
 * IMPL-PLATFORM-F2-P5-6-20260321 Phase B: Rarity display + want-list button
 *
 * Rows: Issues in a series
 * Columns: Variant types (Cover A, B, Ratio, Foil, etc.)
 */
import React, { useState, useEffect, useMemo } from 'react';
import {
  Package, Check, X, Star, Filter, ChevronDown,
  Loader2, AlertCircle, Sparkles, Heart
} from 'lucide-react';

// Rarity configuration — IMPL-PLATFORM-F2-P5-6-20260321 Phase B
const RARITY_CONFIG = {
  common: { color: '#71717A', label: 'Common', tailwind: 'bg-zinc-500' },
  uncommon: { color: '#3B82F6', label: 'Uncommon', tailwind: 'bg-blue-500' },
  rare: { color: '#9333EA', label: 'Rare', tailwind: 'bg-purple-500' },
  ultra_rare: { color: '#D4A03C', label: 'Ultra-Rare', tailwind: 'bg-yellow-600' },
};

// Variant type definitions
const VARIANT_TYPES = [
  { key: 'cover_a', label: 'Cover A', short: 'A' },
  { key: 'cover_b', label: 'Cover B', short: 'B' },
  { key: 'cover_c', label: 'Cover C', short: 'C' },
  { key: 'ratio_1_10', label: '1:10', short: '1:10' },
  { key: 'ratio_1_25', label: '1:25', short: '1:25' },
  { key: 'ratio_1_50', label: '1:50', short: '1:50' },
  { key: 'ratio_1_100', label: '1:100', short: '1:100' },
  { key: 'foil', label: 'Foil', short: 'Foil' },
  { key: 'virgin', label: 'Virgin', short: 'Virg' },
  { key: 'sketch', label: 'Sketch', short: 'Sktch' },
  { key: 'newsstand', label: 'Newsstand', short: 'News' },
  { key: 'signed', label: 'Signed', short: 'Sign' },
];

// Rarity legend — IMPL-PLATFORM-F2-P5-6-20260321 Phase B
function RarityLegend() {
  return (
    <div
      className="flex flex-wrap gap-4 px-3 py-2"
      role="list"
      aria-label="Rarity legend"
    >
      {Object.entries(RARITY_CONFIG).map(([tier, config]) => (
        <div key={tier} className="flex items-center gap-1.5" role="listitem">
          <span
            className="w-2.5 h-2.5 rounded-full inline-block flex-shrink-0"
            style={{ backgroundColor: config.color }}
            aria-hidden="true"
          />
          <span className="text-xs text-zinc-400">{config.label}</span>
        </div>
      ))}
    </div>
  );
}

/**
 * ⛔ VAULT-S4 V-SMART-FE-REQUESTS: the want-list button on a missing-variant cell is DISABLED.
 *
 * POST /vault/smart-sets/{id}/gaps/want is keyed on `comic_issue_id`. This matrix is built
 * from the vault's OWNED items (seriesList below), and a variant the user does not own has no
 * catalog row in that payload -- `issue` carries `issue_number` and the owned `variants` map,
 * never a `comic_issue_id` for the missing cell. The pre-S4 caller posted the ISSUE NUMBER
 * STRING ("300") as if it were an id, so every click was a 422/404 behind a green toast. And
 * the owned copy's `comic_issue_id` is not a substitute: an issue you own is not a gap of
 * any set, so the route would answer 404 "No matching gaps found".
 *
 * The catalog-driven matrix (GET /vault/gaps/variant-matrix/{series_id}/{issue_number} serves
 * `variants[].comic_issue_id`) is the surface that can key a want-list add; this view does not
 * consume it yet. Until it does, the affordance says so instead of pretending.
 */
export const WANT_LIST_UNAVAILABLE = 'Add to want list is unavailable here: a missing variant has no catalog issue id in the vault payload. Use Gap Analysis to add missing issues.';

// Cell in the matrix — enhanced with rarity + want-list (disabled: see WANT_LIST_UNAVAILABLE)
function MatrixCell({ owned, variantType, issue, onClick, rarity }) {
  const rarityConfig = rarity ? RARITY_CONFIG[rarity] : null;
  const rarityLabel = rarityConfig ? rarityConfig.label : '';

  if (owned) {
    return (
      <button
        onClick={() => onClick?.({ issue, variant: variantType, owned: true })}
        className="w-10 h-10 bg-green-500/20 border border-green-500/50 rounded flex items-center justify-center hover:bg-green-500/30 transition-colors relative"
        title={`#${issue.issue_number} ${variantType.label} - Owned`}
        aria-label={`Issue #${issue.issue_number} ${variantType.label}${rarityLabel ? ` - ${rarityLabel}` : ''} - Owned`}
      >
        <Check className="w-4 h-4 text-green-400" />
        {rarityConfig && (
          <span
            className="absolute bottom-0.5 right-0.5 w-1.5 h-1.5 rounded-full"
            style={{ backgroundColor: rarityConfig.color }}
            title={rarityLabel}
            data-testid="rarity-dot"
            aria-hidden="true"
          />
        )}
      </button>
    );
  }

  return (
    <div className="relative group">
      <button
        onClick={() => onClick?.({ issue, variant: variantType, owned: false })}
        className="w-10 h-10 bg-zinc-800/50 border border-zinc-700/50 rounded flex items-center justify-center hover:border-zinc-600 hover:bg-zinc-800 transition-colors relative"
        title={`#${issue.issue_number} ${variantType.label} - Missing`}
        aria-label={`Issue #${issue.issue_number} ${variantType.label} - Missing`}
      >
        <X className="w-3 h-3 text-zinc-600" />
      </button>
      <button
        type="button"
        disabled
        className="absolute -top-1 -right-1 w-5 h-5 bg-pink-500 rounded-full items-center justify-center hidden group-hover:flex transition-colors disabled:opacity-50 disabled:cursor-not-allowed z-10"
        style={{ minWidth: '44px', minHeight: '44px', margin: '-12px' }}
        title={WANT_LIST_UNAVAILABLE}
        aria-label={`Add Issue #${issue.issue_number} ${variantType.label} to want list - unavailable: ${WANT_LIST_UNAVAILABLE}`}
        data-testid="want-list-button"
      >
        <Heart className="w-3 h-3 text-white" />
      </button>
    </div>
  );
}

// Series selector
function SeriesSelector({ series, selectedSeries, onSelect }) {
  const [isOpen, setIsOpen] = useState(false);

  const selected = series.find(s => s.id === selectedSeries);

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg hover:border-zinc-600 transition-colors"
      >
        <span className="text-white font-medium">
          {selected?.name || 'Select Series'}
        </span>
        <ChevronDown className={`w-4 h-4 text-zinc-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-1 w-64 bg-zinc-800 border border-zinc-700 rounded-lg shadow-xl z-20 max-h-64 overflow-y-auto">
          {series.map(s => (
            <button
              key={s.id}
              onClick={() => {
                onSelect(s.id);
                setIsOpen(false);
              }}
              className={`w-full px-4 py-2 text-left hover:bg-zinc-700 transition-colors ${
                s.id === selectedSeries ? 'bg-orange-500/20 text-orange-400' : 'text-white'
              }`}
            >
              <div className="font-medium truncate">{s.name}</div>
              <div className="text-xs text-zinc-400">{s.issueCount} issues</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function VariantMatrixView({ items, onCellClick }) {
  const [selectedSeries, setSelectedSeries] = useState(null);
  const [visibleVariants, setVisibleVariants] = useState(
    VARIANT_TYPES.slice(0, 6).map(v => v.key)
  );
  const [showVariantFilter, setShowVariantFilter] = useState(false);

  // Extract series from items
  const seriesList = useMemo(() => {
    const seriesMap = new Map();

    items.forEach(item => {
      const seriesName = item.comic_issue?.series_name || item.title?.split('#')[0]?.trim();
      const seriesId = item.comic_issue?.series_id || seriesName;

      if (!seriesId) return;

      if (!seriesMap.has(seriesId)) {
        seriesMap.set(seriesId, {
          id: seriesId,
          name: seriesName || 'Unknown Series',
          issues: new Map(),
        });
      }

      const series = seriesMap.get(seriesId);
      const issueNum = item.issue_number || '1';

      if (!series.issues.has(issueNum)) {
        series.issues.set(issueNum, {
          issue_number: issueNum,
          variants: new Map(),
        });
      }

      // Determine variant type from item
      const variantType = detectVariantType(item);
      series.issues.get(issueNum).variants.set(variantType, {
        ...item,
        rarity: item.rarity || null,
      });
    });

    return Array.from(seriesMap.values()).map(s => ({
      ...s,
      issueCount: s.issues.size,
      issues: Array.from(s.issues.values()).sort((a, b) => {
        const numA = parseFloat(a.issue_number) || 0;
        const numB = parseFloat(b.issue_number) || 0;
        return numA - numB;
      }),
    }));
  }, [items]);

  // Auto-select first series
  useEffect(() => {
    if (seriesList.length > 0 && !selectedSeries) {
      setSelectedSeries(seriesList[0].id);
    }
  }, [seriesList, selectedSeries]);

  const currentSeries = seriesList.find(s => s.id === selectedSeries);

  // Detect variant type from item data
  function detectVariantType(item) {
    const desc = (item.variant_description || '').toLowerCase();
    const notes = (item.grade_notes || '').toLowerCase();
    const combined = desc + ' ' + notes;

    if (combined.includes('1:100')) return 'ratio_1_100';
    if (combined.includes('1:50')) return 'ratio_1_50';
    if (combined.includes('1:25')) return 'ratio_1_25';
    if (combined.includes('1:10')) return 'ratio_1_10';
    if (combined.includes('foil')) return 'foil';
    if (combined.includes('virgin')) return 'virgin';
    if (combined.includes('sketch')) return 'sketch';
    if (combined.includes('newsstand')) return 'newsstand';
    if (item.is_signed) return 'signed';
    if (combined.includes('cover c') || combined.includes('variant c')) return 'cover_c';
    if (combined.includes('cover b') || combined.includes('variant b')) return 'cover_b';

    return 'cover_a'; // Default
  }

  // Toggle variant visibility
  const toggleVariant = (variantKey) => {
    setVisibleVariants(prev =>
      prev.includes(variantKey)
        ? prev.filter(v => v !== variantKey)
        : [...prev, variantKey]
    );
  };

  if (seriesList.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <Package className="w-12 h-12 text-zinc-700 mb-3" />
        <p className="text-zinc-400">No variant data to display</p>
        <p className="text-sm text-zinc-500 mt-1">
          Add comics with variant information to see the matrix
        </p>
      </div>
    );
  }

  const displayVariants = VARIANT_TYPES.filter(v => visibleVariants.includes(v.key));

  return (
    <div className="p-4">
      {/* Controls */}
      <div className="flex items-center justify-between mb-4">
        <SeriesSelector
          series={seriesList}
          selectedSeries={selectedSeries}
          onSelect={setSelectedSeries}
        />

        <div className="relative">
          <button
            onClick={() => setShowVariantFilter(!showVariantFilter)}
            className="flex items-center gap-2 px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg hover:border-zinc-600"
          >
            <Filter className="w-4 h-4 text-zinc-400" />
            <span className="text-sm text-zinc-300">Columns</span>
          </button>

          {showVariantFilter && (
            <div className="absolute top-full right-0 mt-1 w-48 bg-zinc-800 border border-zinc-700 rounded-lg shadow-xl z-20 p-2">
              {VARIANT_TYPES.map(variant => (
                <label
                  key={variant.key}
                  className="flex items-center gap-2 px-2 py-1.5 hover:bg-zinc-700 rounded cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={visibleVariants.includes(variant.key)}
                    onChange={() => toggleVariant(variant.key)}
                    className="rounded border-zinc-600 bg-zinc-700 text-orange-500 focus:ring-orange-500"
                  />
                  <span className="text-sm text-zinc-300">{variant.label}</span>
                </label>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Matrix */}
      {currentSeries && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-lg overflow-hidden">
          {/* Header */}
          <div className="flex border-b border-zinc-800">
            <div className="w-20 flex-shrink-0 p-3 bg-zinc-800/50 border-r border-zinc-700">
              <span className="text-xs font-medium text-zinc-400">Issue</span>
            </div>
            {displayVariants.map(variant => (
              <div
                key={variant.key}
                className="flex-1 min-w-[50px] p-2 text-center border-r border-zinc-800 last:border-r-0"
              >
                <span className="text-xs font-medium text-zinc-400" title={variant.label}>
                  {variant.short}
                </span>
              </div>
            ))}
          </div>

          {/* Rows */}
          <div className="max-h-[500px] overflow-y-auto">
            {currentSeries.issues.map((issue, idx) => (
              <div
                key={issue.issue_number}
                className={`flex border-b border-zinc-800/50 last:border-b-0 ${
                  idx % 2 === 0 ? 'bg-zinc-900' : 'bg-zinc-900/50'
                }`}
              >
                {/* Issue number */}
                <div className="w-20 flex-shrink-0 p-2 border-r border-zinc-700 flex items-center">
                  <span className="text-sm font-medium text-white">#{issue.issue_number}</span>
                </div>

                {/* Variant cells */}
                {displayVariants.map(variant => {
                  const variantData = issue.variants.get(variant.key);
                  const isOwned = issue.variants.has(variant.key);

                  return (
                    <div
                      key={variant.key}
                      className="flex-1 min-w-[50px] p-1 flex items-center justify-center border-r border-zinc-800/30 last:border-r-0"
                    >
                      <MatrixCell
                        owned={isOwned}
                        variantType={variant}
                        issue={issue}
                        onClick={onCellClick}
                        rarity={variantData?.rarity || null}
                      />
                    </div>
                  );
                })}
              </div>
            ))}
          </div>

          {/* Summary footer */}
          <div className="p-3 bg-zinc-800/50 border-t border-zinc-700 flex items-center justify-between">
            <div className="text-sm text-zinc-400">
              {currentSeries.issues.length} issues
            </div>
            <div className="flex items-center gap-4 text-sm">
              {displayVariants.map(variant => {
                const count = currentSeries.issues.filter(i => i.variants.has(variant.key)).length;
                const pct = Math.round((count / currentSeries.issues.length) * 100) || 0;
                return (
                  <div key={variant.key} className="flex items-center gap-1">
                    <span className="text-zinc-500">{variant.short}:</span>
                    <span className={pct === 100 ? 'text-green-400' : pct > 50 ? 'text-yellow-400' : 'text-zinc-400'}>
                      {pct}%
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Rarity Legend — IMPL-PLATFORM-F2-P5-6-20260321 Phase B */}
          <div className="border-t border-zinc-700">
            <RarityLegend />
          </div>
        </div>
      )}

      {/* Want-list posture: see WANT_LIST_UNAVAILABLE */}
      <p className="text-xs text-zinc-500 mt-2" data-testid="want-list-unavailable-hint">
        {WANT_LIST_UNAVAILABLE}
      </p>
    </div>
  );
}
